import React from 'react';

import { Badge, Card, Spinner, Text } from '@change/ds-core-ui';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core/react/i18n';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import { checkReduceMotion } from 'src/app/pages/petitionDetails/shared/utilities';
import { useEndorsements } from 'src/app/shared/components/endorsements';
import { ENDORSEMENTS_SECTION_ID } from 'src/app/shared/constants';

const TemporaryEndorsementCardComponent: React.FC = () => {
	const {
		data: { petition },
	} = usePetitionDetailsPageData();

	const {
		data: { endorsements, isLoading, error },
	} = useEndorsements({ petitionSlugOrId: petition.id });

	const { translate } = useI18n();

	if (isLoading) {
		return <Spinner data-testid="temporary-endorsement-card-spinner" className="mx-auto my-16" />;
	}

	if (error || endorsements.length === 0) {
		return null;
	}

	const mostRecent = [...endorsements].sort((a, b) => {
		const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
		const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
		return dateB - dateA;
	})[0];

	const handleClick = () => {
		const prefersReducedMotion = checkReduceMotion();
		const endorsementsSection = document.getElementById(ENDORSEMENTS_SECTION_ID);
		if (endorsementsSection) {
			endorsementsSection.scrollIntoView({
				behavior: prefersReducedMotion ? 'auto' : 'smooth',
			});
		}
	};

	const cardMainText = translate('corgi.petition_details.endorsements.temporary_card.endorsed', {
		title: mostRecent.name,
	});

	return (
		<Card
			as="button"
			onClick={handleClick}
			className="mt-5 mb-4 w-full cursor-pointer gap-[18px] rounded-lg border-0 p-4 px-[20px] pt-[25px] pb-[14px] text-left"
			style={{
				background: 'linear-gradient(84deg, #FEF2F2 0.21%, #FAF8FD 45.88%, #FFFFEB 99.64%)',
				boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.05), 0 3px 19px 0 rgba(58, 41, 62, 0.06)',
			}}
			data-testid="temporary-endorsement-card"
			data-qa="temporary-endorsement-card"
		>
			<Badge
				variant="neutral"
				emphasis="strong"
				size="small"
				label={translate('corgi.petition_details.endorsements.temporary_card.badge_label')}
				className="max-w-[170px]! justify-center [&>span]:text-xs!"
			/>
			<Text
				variant="body"
				size="large"
				className="text-typography-strong flex flex-col leading-[28px] text-wrap md:block"
			>
				<span className="font-semibold">{cardMainText},</span> {mostRecent.createdAtRelativeLocalized}
			</Text>
		</Card>
	);
};

export const TemporaryEndorsementCard = withI18nPlaceholders(I18N_PLACEHOLDERS, TemporaryEndorsementCardComponent);
